/**
 * Never let the map collapse below this, so a very short window scrolls rather than
 * shrinking the map to a sliver the floating card cannot fit in.
 */
export const MIN_MAP_HEIGHT_PX = 320;

/**
 * How tall the recording map must be to fill the window below wherever it starts.
 *
 * The map used to be a fixed 84svh, which only fits when the header and breadcrumb bar
 * above it come to exactly 16% of the window. On a shorter window they are a larger
 * share, so the map ran past the bottom edge and cut off the floating recording card
 * and its elevation chart. Measuring the map's own top avoids hard-coding a header
 * height that changes with the breakpoint.
 *
 * `mapTop` is the map's distance from the top of the document, not the viewport, so
 * the answer doesn't change when the page has been scrolled.
 */
export function mapHeightBelow(viewportHeight: number, mapTop: number): number {
  return Math.max(MIN_MAP_HEIGHT_PX, Math.round(viewportHeight - mapTop));
}

/**
 * Every element laid out above `el` in the page — its preceding siblings and those of each
 * ancestor, up to <body>. These are what push the map down, so their height changes are
 * what the map has to re-measure for. Watching <body> itself doesn't work: it is pinned to
 * the viewport height, so it never changes size when the header does.
 */
export function elementsAbove(el: Element | null | undefined): Element[] {
  const found: Element[] = [];
  let node: Element | null = el ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    let sibling = node.previousElementSibling;
    while (sibling) {
      found.push(sibling);
      sibling = sibling.previousElementSibling;
    }
    node = node.parentElement;
  }
  return found;
}
